import "./styles.css";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useLangStore from "@/hooks/useLangStore";
import useFetch from "@/hooks/useFetch";
import usePageSize from "@/hooks/usePageSize";
import usePopupStore from "@/hooks/usePopupStore";
import translations from "@/config/translations";
import Table, { type TableColumn } from "@/UI/components/Table";
import TableCell from "@/UI/components/Table/TableCell";
import SidePopup from "@/UI/popups/SidePopup";
import CategoriesForm from "@/UI/forms/CategoriesForm";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";

interface Category {
    _id: string;
    name: string;
    description: string;
    picture: string | null;
    sort: number;
}

function parseUrlFilters(searchParams: URLSearchParams): Record<string, string> {
    const result: Record<string, string> = {};
    const search = searchParams.get("search");
    if (search) result["search"] = search;
    return result;
}

export default function ProductsCategoriesScreen() {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const { addComp, removeComp } = usePopupStore();
    const [page, setPage] = useState(1);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const { pageSize, setPageSize } = usePageSize();
    const [searchParams] = useSearchParams();

    // ── Categories fetch ──────────────────────────────────────────────────────
    const { data, loading, invoke } = useFetch("products/categories", { query: { page: 1, limit: pageSize, ...parseUrlFilters(searchParams) } });

    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        invoke({ query: { page, limit: pageSize, ...parseUrlFilters(searchParams) } });
    }, [page]);

    useEffect(() => {
        const handler = () => {
            setPage(1);
            invoke({ query: { page: 1, limit: pageSize, ...parseUrlFilters(searchParams) } });
        };
        window.addEventListener("categories:refresh", handler);
        return () => window.removeEventListener("categories:refresh", handler);
    }, []);

    // Listen for filter events dispatched from ProductsScreen
    useEffect(() => {
        const handler = (e: CustomEvent) => {
            setPage(1);
            invoke({ query: { page: 1, limit: pageSize, ...e.detail } });
        };
        window.addEventListener("categories:filter", handler as EventListener);
        return () => window.removeEventListener("categories:filter", handler as EventListener);
    }, [pageSize]);

    const categories: Category[] = data?.data?.categories ?? [];
    const total: number = data?.data?.total ?? 0;

    const handlePageSizeChange = (size: number) => {
        setPage(1);
        setPageSize(size);
        invoke({ query: { page: 1, limit: size, ...parseUrlFilters(searchParams) } });
    };

    // ── Delete fetch ──────────────────────────────────────────────────────────
    const { loading: deleting, status: deleteStatus, invoke: deleteInvoke } = useFetch("products/categories", { method: "DELETE" }, false);

    const wasDeleting = useRef(false);
    useEffect(() => {
        if (deleting) {
            wasDeleting.current = true;
        } else if (wasDeleting.current) {
            wasDeleting.current = false;
            setDeletingId(null);
            if (deleteStatus === 200) invoke({ query: { page, limit: pageSize, ...parseUrlFilters(searchParams) } });
        }
    }, [deleting]);

    const handleDelete = (id: string) => {
        if (deleting) return;
        setDeletingId(id);
        deleteInvoke({ param: id });
    };

    const openDeleteConfirm = (category: Category) => {
        const name = "confirm-delete-category";
        removeComp(name);
        addComp({
            name,
            Component: ConfirmPopup,
            details: {
                message: t.categories.deleteConfirm,
                icon: "/icons/delete-icon.svg",
                variant: "danger",
                onConfirm: () => handleDelete(category._id),
            },
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    // ── Get-one fetch (edit / duplicate) ──────────────────────────────────────
    const pendingModeRef = useRef<"edit" | "duplicate" | null>(null);
    const { loading: fetchingOne, data: oneData, invoke: fetchOne } = useFetch("products/categories", { method: "GET" }, false);

    const openCategoryForm = (mode: "edit" | "duplicate", category: any) => {
        const name = mode === "edit" ? "edit-category" : "duplicate-category";
        const title = mode === "edit" ? t.categoryForm.editTitle : t.categoryForm.duplicateTitle;
        removeComp(name);
        addComp({
            name,
            Component: SidePopup,
            details: {
                title,
                Content: CategoriesForm,
                contentDetails: {
                    categoryId: mode === "edit" ? category._id : undefined,
                    initialData: category,
                    isDuplicate: mode === "duplicate",
                    onSuccess: () => {
                        invoke({ query: { page: 1, limit: pageSize, ...parseUrlFilters(searchParams) } });
                        setPage(1);
                    },
                },
            },
            place: "end",
            anim: "endSlide",
            cover: "shaded",
            coverClose: true,
        });
    };

    const wasFetchingOne = useRef(false);
    useEffect(() => {
        if (fetchingOne) {
            wasFetchingOne.current = true;
        } else if (wasFetchingOne.current) {
            wasFetchingOne.current = false;
            const category = oneData?.data?.category;
            if (category && pendingModeRef.current) {
                openCategoryForm(pendingModeRef.current, category);
                pendingModeRef.current = null;
            }
        }
    }, [fetchingOne]);

    const handleEdit = (row: Category) => {
        pendingModeRef.current = "edit";
        fetchOne({ param: row._id });
    };

    const handleDuplicate = (row: Category) => {
        pendingModeRef.current = "duplicate";
        fetchOne({ param: row._id });
    };

    // ── Columns ───────────────────────────────────────────────────────────────
    const columns: TableColumn<Category>[] = [
        {
            key: "picture",
            header: t.categories.col.image,
            width: "76px",
            render: (row) => <TableCell.Gallery images={row.picture ? [row.picture] : []} alt={row.name} />,
        },
        {
            key: "name",
            header: t.categories.col.name,
            width: "200px",
            render: (row) => <TableCell.Text value={row.name} maxChars={25} />,
        },
        {
            key: "description",
            header: t.categories.col.description,
            width: "250px",
            render: (row) => <TableCell.Description value={row.description || "—"} title={t.categories.col.description} maxChars={30} />,
        },
        {
            key: "sort",
            header: t.categories.col.sort,
            width: "90px",
            render: (row) => <TableCell.Number value={row.sort} />,
        },
        {
            key: "actions",
            header: t.categories.col.actions,
            width: "60px",
            render: (row) => (
                <TableCell.Actions
                    disabled={deletingId === row._id || fetchingOne}
                    actions={[
                        { name: "edit", icon: "/icons/edit-icon.svg", handler: () => handleEdit(row) },
                        { name: "duplicate", icon: "/icons/duplicate-icon.svg", handler: () => handleDuplicate(row) },
                        { name: "delete", icon: "/icons/delete-icon.svg", handler: () => openDeleteConfirm(row), danger: true },
                    ]}
                />
            ),
        },
    ];

    return (
        <div className="products-categories-screen">
            <Table
                columns={columns}
                data={categories}
                loading={loading}
                rowKey="_id"
                pagination={{ page, pageSize, total, onPageChange: setPage, onPageSizeChange: handlePageSizeChange }}
            />
        </div>
    );
}
